import { TOKEN_EXPIRY_S } from './constants.js';

/* ── Token signing (serverless) ────────────────── */

function base64UrlEncode(data) {
    const text = typeof data === 'string' ? data : String.fromCharCode(...new Uint8Array(data));
    return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(str) {
    const padded = str + '='.repeat((4 - (str.length % 4)) % 4);
    const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

let cachedKey = null;

async function ensureKey() {
    if (!cachedKey) {
        const raw = crypto.getRandomValues(new Uint8Array(32));
        cachedKey = await crypto.subtle.importKey(
            'raw',
            raw,
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign', 'verify']
        );
    }
    return cachedKey;
}

export async function createToken(payload) {
    const key = await ensureKey();
    const now = Math.floor(Date.now() / 1000);

    const header = base64UrlEncode(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const body = base64UrlEncode(
        JSON.stringify({ ...payload, iat: now, exp: now + TOKEN_EXPIRY_S })
    );

    const data = new TextEncoder().encode(`${header}.${body}`);
    const signature = await crypto.subtle.sign('HMAC', key, data);
    return `${header}.${body}.${base64UrlEncode(signature)}`;
}

export async function verifyToken(token) {
    const key = await ensureKey();
    const [header, body, sig] = token.split('.');
    if (!header || !body || !sig) return null;

    let data;
    let signature;
    try {
        data = new TextEncoder().encode(`${header}.${body}`);
        signature = base64UrlDecode(sig);
    } catch {
        return null;
    }

    if (!(await crypto.subtle.verify('HMAC', key, signature, data))) return null;

    const decoded = JSON.parse(new TextDecoder().decode(base64UrlDecode(body)));
    if (decoded.exp && decoded.exp < Date.now() / 1000) return null;
    return decoded;
}

export function decodeToken(token) {
    const [, body] = token.split('.');
    if (!body) return null;
    return JSON.parse(new TextDecoder().decode(base64UrlDecode(body)));
}

export function toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

/* ── Transport (serverless / server) ───────────── */

export function createTransport(mode, options = {}) {
    if (mode === 'serverless') return createServerlessTransport(options);
    const baseUrl = mode === 'custom' ? options.server : 'https://api.openage.dev';
    return createServerTransport(baseUrl, options);
}

function createServerlessTransport() {
    return {
        async verify(payload) {
            const { estimatedAge, livenessOk } = payload;
            if (!livenessOk || estimatedAge == null) return { success: false, token: null };

            const token = await createToken({ estimatedAge, livenessOk: true, mode: 'serverless' });
            return { success: true, token };
        },
        close() {},
    };
}

function createServerTransport(baseUrl, options) {
    let session = null;
    let channel = null;

    return {
        async createSession() {
            const transports = [];
            if (typeof WebSocket !== 'undefined') transports.push('websocket');
            transports.push('poll');

            const response = await fetch(`${baseUrl}/api/session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sitekey: options.sitekey,
                    action: options.action,
                    supportedTransports: transports,
                }),
            });

            if (!response.ok) throw new Error('Request failed');
            session = await response.json();
            return session;
        },

        openChannel() {
            if (!session) throw new Error('No session');
            channel =
                session.transport === 'websocket'
                    ? createWsChannel(baseUrl, session.sessionId)
                    : createPollChannel(baseUrl, session.sessionId);
        },

        async receive() {
            return channel ? channel.receive() : null;
        },

        async send(data) {
            return channel ? channel.send(data) : undefined;
        },

        async sendAndReceive(data) {
            return channel ? channel.sendAndReceive(data) : null;
        },

        async verify(payload) {
            if (channel) {
                await channel.send(payload);
                return channel.receive();
            }

            const response = await fetch(`${baseUrl}/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    response: payload.token,
                    sitekey: options.sitekey,
                    action: options.action,
                }),
            });

            if (!response.ok) return { success: false, token: null };
            return response.json();
        },

        getSession() {
            return session;
        },

        close() {
            channel?.close();
            channel = null;
            session = null;
        },
    };
}

function createWsChannel(baseUrl, sessionId) {
    const wsUrl = baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const ws = new WebSocket(`${wsUrl}/api/ws/${sessionId}`);
    let pending = [];
    let closed = false;

    const waitReady = new Promise((resolve, reject) => {
        ws.onopen = resolve;
        ws.onerror = () => reject(new Error('Connection failed'));
    });

    ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        pending.shift()?.(message);
    };

    ws.onclose = () => {
        closed = true;
        for (const resolver of pending) resolver(null);
        pending = [];
    };

    return {
        receive() {
            if (closed) return Promise.resolve(null);
            return new Promise((resolve) => pending.push(resolve));
        },
        async send(data) {
            await waitReady;
            ws.send(JSON.stringify(data));
        },
        async sendAndReceive(data) {
            await waitReady;
            ws.send(JSON.stringify(data));
            return this.receive();
        },
        close() {
            closed = true;
            ws.close();
        },
    };
}

function createPollChannel(baseUrl, sessionId) {
    const post = async (data) => {
        const response = await fetch(`${baseUrl}/api/verify/${sessionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Request failed');
        return response;
    };

    return {
        async receive() {
            const response = await fetch(`${baseUrl}/api/poll/${sessionId}`);
            if (!response.ok) throw new Error('Request failed');
            return response.json();
        },
        async send(data) {
            await post(data);
        },
        async sendAndReceive(data) {
            return (await post(data)).json();
        },
        close() {},
    };
}

/* ── WASM VM client ────────────────────────────── */

let wasmModule = null;
let vmSession = null;
let challengeBundle = null;
let faceData = null;
let challengeParams = null;
let bridge = null;

const decryptedCache = new Map();

function loadScript(url) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${url}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = url;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Failed to load ${url}`));
        document.head.appendChild(script);
    });
}

function readU32(mod, ptr) {
    return (
        mod.HEAPU8[ptr] |
        (mod.HEAPU8[ptr + 1] << 8) |
        (mod.HEAPU8[ptr + 2] << 16) |
        (mod.HEAPU8[ptr + 3] << 24)
    );
}

export async function initVM(session) {
    vmSession = session;

    await loadScript(session.wasmJs);
    const loaderModule = await import(session.loaderJs);
    wasmModule = await loaderModule.initModule(session.wasmBin);

    if (wasmModule[`_${session.exports.vm_init}`]() !== 0) throw new Error('VM init failed');

    const bundleResponse = await fetch(session.challengeVmbc);
    challengeBundle = new Uint8Array(await bundleResponse.arrayBuffer());
}

export async function decryptModel(session, modelId) {
    if (!wasmModule || !vmSession) throw new Error('VM not loaded');

    const modelInfo = session.models[modelId];
    if (!modelInfo) throw new Error(`Unknown model: ${modelId}`);

    const response = await fetch(modelInfo.url);
    if (!response.ok) throw new Error(`Failed to fetch model: ${modelId}`);

    const encrypted = new Uint8Array(await response.arrayBuffer());
    const length = encrypted.length;

    const inputPtr = wasmModule._malloc(length);
    wasmModule.HEAPU8.set(encrypted, inputPtr);

    const outLenPtr = wasmModule._malloc(4);
    const outPtr = wasmModule[`_${session.exports.vm_decrypt_blob}`](inputPtr, length, outLenPtr);
    wasmModule._free(inputPtr);

    if (!outPtr) {
        wasmModule._free(outLenPtr);
        throw new Error(`Decryption failed: ${modelId}`);
    }

    const outLen = readU32(wasmModule, outLenPtr);
    wasmModule._free(outLenPtr);

    const result = new Uint8Array(outLen);
    result.set(wasmModule.HEAPU8.subarray(outPtr, outPtr + outLen));
    wasmModule[`_${session.exports.vm_free}`](outPtr);
    return result.buffer;
}

function defineVmGlobal(name, getter) {
    try {
        delete window[name];
    } catch (_) {}
    Object.defineProperty(window, name, { get: getter, set() {}, configurable: true });
}

export function setFaceData(data) {
    faceData = data;
    defineVmGlobal('__vmFaceData', () => faceData);
}

export function setChallengeParams(params) {
    challengeParams = params;
    defineVmGlobal('__vmChallenge', () => challengeParams);
}

export function registerBridge(handlers) {
    bridge = Object.freeze({ ...handlers });
    defineVmGlobal('__vmBridge', () => bridge);
}

export function unregisterBridge() {
    bridge = null;
    try {
        delete window.__vmBridge;
    } catch (_) {}
}

export function executeChallenge() {
    if (!wasmModule || !challengeBundle) throw new Error('VM not loaded');

    const length = challengeBundle.length;
    const inputPtr = wasmModule._malloc(length);
    wasmModule.HEAPU8.set(challengeBundle, inputPtr);

    const outLenPtr = wasmModule._malloc(4);
    const outPtr = wasmModule[`_${vmSession.exports.vm_exec_bytecode}`](
        inputPtr,
        length,
        outLenPtr
    );
    wasmModule._free(inputPtr);

    if (!outPtr) {
        wasmModule._free(outLenPtr);
        throw new Error(lastVmError() || 'VM execution failed');
    }

    const outLen = readU32(wasmModule, outLenPtr);
    wasmModule._free(outLenPtr);

    const result = new Uint8Array(outLen);
    result.set(wasmModule.HEAPU8.subarray(outPtr, outPtr + outLen));
    wasmModule[`_${vmSession.exports.vm_free}`](outPtr);
    return result;
}

function lastVmError() {
    const errFn = vmSession.exports.vm_last_error;
    if (!errFn) return null;
    const errPtr = wasmModule[`_${errFn}`]();
    return errPtr ? wasmModule.UTF8ToString(errPtr) : null;
}

export function destroyVM() {
    if (!wasmModule || !vmSession) return;

    if (vmSession.exports.vm_destroy) wasmModule[`_${vmSession.exports.vm_destroy}`]();

    wasmModule = null;
    vmSession = null;
    challengeBundle = null;
    faceData = null;
    challengeParams = null;
    bridge = null;

    for (const name of ['__vmFaceData', '__vmChallenge', '__vmBridge']) {
        try {
            delete window[name];
        } catch (_) {}
    }
}

export function isVMLoaded() {
    return wasmModule !== null && vmSession !== null;
}

async function decryptAndCache(session, modelId) {
    if (decryptedCache.has(modelId)) return decryptedCache.get(modelId);
    const buffer = await decryptModel(session, modelId);
    decryptedCache.set(modelId, buffer);
    return buffer;
}

export async function ensureModels(session, onProgress) {
    await decryptAndCache(session, 'mediapipe');
    onProgress?.(1);
}

export async function getMediaPipeModelBuffer(session) {
    return decryptAndCache(session, 'mediapipe');
}

export function clearModelCache() {
    decryptedCache.clear();
}
